import React, { useMemo } from "react";
import { nf } from "../lib/format";

const ROW_BG_A = "#0b121c";
const ROW_BG_B = "#121a26";
const rowBg = (i) => (i % 2 === 0 ? ROW_BG_A : ROW_BG_B);

export default function CountsTable({
  rows,
  firstColKey,
  firstColHeader,
  visible,
  onFirstCol,
  onCell,
  hideZeroRows = true,
}) {
  const cell = { padding: "8px 10px", borderBottom: "1px solid #1f2834", fontSize: 14 };
  const th = { ...cell, color: "#9ec3ff", fontWeight: 700, textAlign: "left", background: "#0b111a" };
  const tdR = { ...cell, textAlign: "right", color: "#c6d2e3" };
  const tdL = { ...cell, textAlign: "left", color: "#c6d2e3" };

  const clickable = { cursor: "pointer", color: "#dce7f5" };
  const disabled   = { color: "#566275", cursor: "default" };

  // ⬆️ Unified emphasis style for ALL totals (column + footer)
  const totalEmphasis = { color: "#ffffff", fontWeight: 800 };

  const visibleRowTotal = (row) =>
    (visible.cam41 ? Number(row["41"] ?? 0) : 0) +
    (visible.chel ? Number(row["Cheltenham"] ?? 0) : 0) +
    (visible.pool ? Number(row["Pool"] ?? 0) : 0);

  const renderRows = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    return hideZeroRows ? base.filter((r) => visibleRowTotal(r) > 0) : base;
  }, [rows, hideZeroRows, visible]);

  const totals = useMemo(() => {
    let t41 = 0, tChel = 0, tPool = 0, tAll = 0;
    for (const r of renderRows) {
      const v41  = Number(r["41"] ?? 0);
      const vChel= Number(r["Cheltenham"] ?? 0);
      const vPool= Number(r["Pool"] ?? 0);
      if (visible.cam41) t41  += v41;
      if (visible.chel) tChel += vChel;
      if (visible.pool) tPool += vPool;
      tAll += (visible.cam41 ? v41 : 0) + (visible.chel ? vChel : 0) + (visible.pool ? vPool : 0);
    }
    return { t41, tChel, tPool, tAll };
  }, [renderRows, visible]);

  const renderNumCell = (r, key) => {
    const v = key === "Total" ? visibleRowTotal(r) : Number(r[key] ?? 0);
    const canClick = v > 0;

    // Base style for numeric cells
    let style = { ...tdR, ...(canClick ? clickable : disabled) };

    // Emphasize the entire Total column
    if (key === "Total") {
      style = { ...style, ...totalEmphasis };
    }

    return (
      <td
        style={style}
        onClick={canClick && onCell ? () => onCell(r, key) : undefined}
        title={canClick ? "Open sightings for this cell" : undefined}
      >
        {nf.format(v)}
      </td>
    );
  };

  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={th}>{firstColHeader}</th>
          {visible.cam41 && <th style={{ ...th, textAlign: "right" }}>41</th>}
          {visible.chel && <th style={{ ...th, textAlign: "right" }}>Cheltenham</th>}
          {visible.pool && <th style={{ ...th, textAlign: "right" }}>Pool</th>}
          {/* Keep Total header clear; body/footer apply the bright emphasis */}
          <th style={{ ...th, textAlign: "right" }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {renderRows.map((r, i) => (
          <tr key={i} style={{ background: rowBg(i) }}>
            <td
              style={{ ...tdL, cursor: onFirstCol ? "pointer" : "default", color: "#e0e8f5" }}
              onClick={onFirstCol ? () => onFirstCol(r) : undefined}
              title="Select this period (no redirect)"
            >
              {r[firstColKey]}
            </td>
            {visible.cam41 && renderNumCell(r, "41")}
            {visible.chel && renderNumCell(r, "Cheltenham")}
            {visible.pool && renderNumCell(r, "Pool")}
            {renderNumCell(r, "Total")}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td style={{ ...tdL, ...totalEmphasis }}>Total</td>
          {visible.cam41 && <td style={{ ...tdR, ...totalEmphasis }}>{nf.format(totals.t41)}</td>}
          {visible.chel &&  <td style={{ ...tdR, ...totalEmphasis }}>{nf.format(totals.tChel)}</td>}
          {visible.pool &&  <td style={{ ...tdR, ...totalEmphasis }}>{nf.format(totals.tPool)}</td>}
          <td style={{ ...tdR, ...totalEmphasis }}>{nf.format(totals.tAll)}</td>
        </tr>
      </tfoot>
    </table>
  );
}
